import type { TokenProvider } from '../../http/tokenProvider.js';
import type { ChatEvent, ChatScope } from './types.js';

export interface OpenChatStreamOptions {
  scope: ChatScope;
  conversationId: string;
  onEvent: (event: ChatEvent) => void;
  onError?: (err: Event) => void;
  lastEventId?: string;
  /** Heartbeat in ms before the polyfill considers the connection stale. Default: 60000. */
  heartbeatTimeout?: number;
}

export interface ChatStreamHandle {
  close(): void;
}

export interface ChatStreamDeps {
  baseUrl: string;
  getToken?: TokenProvider;
}

const STREAM_PATH = '/api/agentengine/v1/agent/chat/sessions';

/**
 * Opens an SSE connection to the chat stream. Requires the consumer to have
 * `event-source-polyfill` installed (peer dep). Browsers' native EventSource
 * does not support custom headers, which we need for auth + scope.
 */
export async function openChatStream(
  deps: ChatStreamDeps,
  options: OpenChatStreamOptions,
): Promise<ChatStreamHandle> {
  // Lazy import so consumers in non-browser contexts (or those who don't use
  // the chat stream at all) aren't forced to install the polyfill.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mod: any = await import('event-source-polyfill');
  const Polyfill = mod.EventSourcePolyfill ?? mod.default?.EventSourcePolyfill ?? mod.default;
  if (!Polyfill) {
    throw new Error('event-source-polyfill is required for client.chat.openStream(). Install it as a peer dep.');
  }

  const url = `${deps.baseUrl.replace(/\/$/, '')}${STREAM_PATH}/${options.conversationId}/stream`;

  const headers: Record<string, string> = {
    'X-Org-Id': options.scope.orgId,
    'X-User-Id': options.scope.userId,
    'X-Sandbox-Id': options.scope.sandboxId ?? options.scope.repoId ?? '',
    'X-Repo-Id': options.scope.sandboxId ?? options.scope.repoId ?? '',
  };
  if (options.scope.projectId ?? options.scope.workspaceId) {
    headers['X-Project-Id'] = options.scope.projectId ?? options.scope.workspaceId ?? '';
    headers['X-Workspace-Id'] = options.scope.projectId ?? options.scope.workspaceId ?? '';
  }
  if (deps.getToken) {
    const token = await deps.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  if (options.lastEventId) headers['Last-Event-ID'] = options.lastEventId;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const es = new (Polyfill as any)(url, {
    headers,
    heartbeatTimeout: options.heartbeatTimeout ?? 60_000,
  });

  // The backend sends named SSE events (event: phase, event: question, …).
  // onmessage only fires for the default unnamed type; addEventListener is required for named events.
  const KNOWN_EVENTS = [
    'phase', 'question', 'plan_draft',
    'step_started', 'step_completed', 'step_failed', 'step_progress',
    'narration', 'assistant_message', 'done',
    'session_title',
    'tool_call_started', 'tool_call_finished',
    'llm_call_finished',
    'tool_call_confirmation_required',
  ];

  const handleRaw = (raw: MessageEvent) => {
    try {
      const parsed = JSON.parse(raw.data) as ChatEvent;
      options.onEvent(parsed);
    } catch {
      // Ignore malformed events
    }
  };

  for (const name of KNOWN_EVENTS) {
    es.addEventListener(name, handleRaw);
  }

  if (options.onError) {
    es.onerror = (event: Event) => options.onError?.(event);
  }

  return { close: () => es.close() };
}
